import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import { startCase } from 'lodash';
import React, { useCallback, useState } from 'react';

import { ScheduledOutreachData } from 'shared/models/scheduled-outreach';

import {
    JOB_OUTREACH_CHECKS,
    JobOutreachCheckRecord,
    JobSendOutreachData,
    JobSendRevivalData,
    READY_CANDIDATES_CACHE,
    REFRESH_READY_CANDIDATES_CACHE,
    REFRESH_REVIVAL_CANDIDATES_CACHE,
    REVIVAL_CANDIDATES_CACHE,
    SCHEDULE_OUTREACH
} from '../graphql/queries/outreach';
import { useModal } from '../hooks/use-modal';
import { useSession } from '../hooks/use-session';
import { useSnackbar } from '../hooks/use-snackbar';
import { OutreachSendDialog } from './outreach-send-dialog';

export const OutreachSendDialogContainer: React.FC<{
    open: boolean;
    onClose: () => void;
    client: { name: string };
    jobId: string;
    dailyEmailLimit: number;
}> = ({ open, onClose, client, jobId, dailyEmailLimit }) => {
    const { user } = useSession();
    const { setAlert, getConfirmation } = useModal();
    const [revival, setRevival] = useState(false);
    const { data: jobData, error: jobChecksError } = useQuery<
        {
            job: JobOutreachCheckRecord;
        },
        { jobId: string }
    >(JOB_OUTREACH_CHECKS, { variables: { jobId } });
    const [fetchReadyCandidates, { data: readyCandidates, error: readyCandidatesError }] = useLazyQuery<
        { job: JobSendOutreachData | null },
        { jobId: string; assigneeId: string }
    >(READY_CANDIDATES_CACHE, { fetchPolicy: 'network-only' });
    const [fetchRevivalCandidates, { data: revivalCandidates, error: revivalCandidatesError }] = useLazyQuery<
        { job: JobSendRevivalData | null },
        { jobId: string; revivalReason: string }
    >(REVIVAL_CANDIDATES_CACHE, { fetchPolicy: 'network-only' });
    const [refreshReadyCandidates, { loading: refreshReadyCandidatesLoading }] = useMutation<
        {},
        { jobId: string; userId: string; minAgeMs?: number }
    >(REFRESH_READY_CANDIDATES_CACHE);
    const [refreshRevivalCandidates, { loading: refreshRevivalCandidatesLoading }] = useMutation<
        {},
        { jobId: string; revivalReason: string; minAgeMs?: number }
    >(REFRESH_REVIVAL_CANDIDATES_CACHE);
    const [scheduleOutreach, { loading: scheduling }] = useMutation<{}, { outreach: Partial<ScheduledOutreachData> }>(
        SCHEDULE_OUTREACH
    );
    const { setSnackbar } = useSnackbar();

    React.useEffect(() => {
        if (jobChecksError || readyCandidatesError || revivalCandidatesError) {
            setAlert(
                'Error fetching data',
                'There was a network error when fetching candidates data. Please make sure you are connected to the internet and try again.',
                false,
                false,
                onClose
            );
        }
    }, [jobChecksError, readyCandidatesError, revivalCandidatesError]);

    const checkJobDetailsExistence = useCallback(() => {
        if (jobData.job.jobType === 'S') return true;

        if (!jobData.job.onePager) {
            setAlert('One pager missing', 'Account manager needs to create the one pager before outreach can be sent');
            return false;
        }

        const job = jobData.job;
        const jobDescription = job.description;

        const requiredJobFields: Array<keyof JobOutreachCheckRecord> = [
            'placementFees',
            'recruiterPercentage',
            'accountManagerPercentage'
        ];
        const requiredJobDescriptionFields: Array<keyof JobOutreachCheckRecord['description']> = [
            'compensation',
            'description',
            'hiringManager',
            'visa'
        ];
        const missingJobFields = requiredJobFields.filter(
            (field) => job[field] === undefined || job[field] === '' || job[field] === null
        );
        const missingJobDescriptionFields = requiredJobDescriptionFields.filter(
            (field) =>
                jobDescription[field] === undefined || jobDescription[field] === '' || jobDescription[field] === null
        );
        const missingFields = [...missingJobFields, ...missingJobDescriptionFields];
        if (missingFields.length > 0) {
            const list = missingFields.map((field) => <li key={field}>{startCase(field)}</li>);
            const description = (
                <div>
                    <ul>{list}</ul>
                    <div style={{ marginTop: '25px' }}>
                        <a href={`/job/${jobId}/edit`} target="_blank">
                            Edit Job Settings
                        </a>
                    </div>
                </div>
            );
            setAlert('Job details missing fields', description);
            return false;
        }
        return true;
    }, [jobData, setAlert, jobId]);

    React.useEffect(() => {
        if (jobData && checkJobDetailsExistence()) {
            fetchReadyCandidates({ variables: { jobId, assigneeId: user.id } });
        }
    }, [jobData, checkJobDetailsExistence, fetchReadyCandidates, jobId, user.id]);

    if (!jobData?.job) return null;

    const handleRefreshCounts = (revivalReason?: string) => {
        getConfirmation(
            async () => {
                if (revival) {
                    await refreshRevivalCandidates({ variables: { jobId, revivalReason } });
                    fetchRevivalCandidates({ variables: { jobId, revivalReason } });
                } else {
                    await refreshReadyCandidates({ variables: { jobId, userId: user.id } });
                    fetchReadyCandidates({ variables: { jobId, assigneeId: user.id } });
                }
            },
            'This will re-calculate the outreach counts, can take a few minutes. Are you sure you want to continue?',
            'Refresh Counts'
        );
    };

    const handleRevivalSelect = (revivalActive: boolean, reason: string) => {
        setRevival(revivalActive);
        if (revivalActive) {
            fetchRevivalCandidates({ variables: { jobId, revivalReason: reason } });
        }
    };

    const handleSend = async (data: {
        limit: number;
        revivalReason?: string;
        scheduledAt: number;
        sequenceId: string;
    }) => {
        setSnackbar('Scheduling outreach');
        const { limit, revivalReason, scheduledAt, sequenceId } = data;
        await scheduleOutreach({
            variables: { outreach: { limit, scheduledAt, revivalReason, sequenceId, jobId, userId: user.id } }
        });
        onClose();
        setSnackbar('Outreach scheduled');
    };

    const readySummary = readyCandidates?.job;
    const revivalSummary = revivalCandidates?.job;
    const activeSummary = revival ? revivalSummary : readySummary;
    const countsCachedAt = activeSummary?.refreshedAt;

    const availableCount =
        refreshReadyCandidatesLoading || refreshRevivalCandidatesLoading
            ? undefined
            : activeSummary === undefined
              ? undefined
              : (activeSummary?.availableCount ?? 0);
    const person = activeSummary?.samplePerson ?? undefined;
    const phoneCallSchedulingLink = user.calendarLink;
    const dialog =
        open && !scheduling ? (
            <OutreachSendDialog
                client={client}
                jobId={jobId}
                sampleRecipient={person}
                availableCount={availableCount}
                dailyEmailLimit={dailyEmailLimit}
                dailyPerUserEmailLimit={jobData.job.dailyPerUserEmailLimit}
                countsCachedAt={countsCachedAt}
                onClose={onClose}
                onSend={handleSend}
                onRefreshCounts={handleRefreshCounts}
                onRevivalSelect={handleRevivalSelect}
                phoneCallSchedulingLink={phoneCallSchedulingLink}
            />
        ) : null;
    return dialog;
};
